import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { getApiErrorMessage } from "@/lib/errors";
import type { UpdateClaimRequest } from "@/api/generated/client/models/UpdateClaimRequest";
import type { AssignedUser } from "@/api/generated/client/models/AssignedUser";

export const CLAIMS_PAGE_SIZE = 50;

export function useSharedClaimFilterOptions(archivedOnly = false) {
    const { firebaseUser, user } = useAuth();
    const { data, isLoading, error } = useQuery({
        queryKey: ["claims", "shared-filter-options", user?.id, user?.companyId, archivedOnly],
        queryFn: () => apiClient.claims.getSharedClaimFilterOptions({ archivedOnly }),
        enabled: !!firebaseUser && !!user,
    });
    const options = data?.data ?? [];
    const pipelines = Array.from(new Map(options.map((option) => [option.pipelineId, {
        id: option.pipelineId,
        name: `${option.pipelineName} (${option.companyName})`,
        color: option.pipelineColor,
    }])).values());
    const statuses = Array.from(new Map(options.flatMap((option) =>
        option.statusTemplateId != null && option.statusName != null ? [[option.statusTemplateId, {
            id: option.statusTemplateId,
            pipelineId: option.pipelineId,
            displayName: option.statusName,
            color: option.statusColor,
        }] as const] : []
    )).values());

    return { pipelines, statuses, isLoading, error };
}

/**
 * Fetches the distinct users assigned across the caller's accessible claims,
 * used to populate the assignee filter on the claims list. Gated on auth
 * readiness to avoid token-provider errors during initial load.
 */
export function useClaimAssigneeOptions() {
    const { firebaseUser } = useAuth();

    const { data, isLoading } = useQuery({
        queryKey: ["claims", "assignees"],
        queryFn: () => apiClient.claims.getClaimAssignees(),
        enabled: !!firebaseUser,
    });

    const assigneeOptions: AssignedUser[] = data?.data ?? [];

    return { assigneeOptions, isLoading };
}

export interface ClaimsFilters {
    page?: number;
    limit?: number;
    search?: string;
    claimStatusTemplateId?: number;
    pipelineId?: number;
    claimFolderId?: number;
    assigneeUserId?: number;
    includeArchived?: boolean;
    archivedOnly?: boolean;
}

export function useClaims(filters: ClaimsFilters = {}) {
    const { page = 1, limit = CLAIMS_PAGE_SIZE, search, claimStatusTemplateId, pipelineId, claimFolderId, assigneeUserId, includeArchived = false, archivedOnly = false } = filters;
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data: claimsResponse, isLoading: isLoadingClaims, isPlaceholderData } = useQuery({
        queryKey: ["claims", page, limit, search, claimStatusTemplateId, pipelineId, claimFolderId, assigneeUserId, includeArchived, archivedOnly],
        queryFn: async () => {
            const response = await apiClient.claims.listClaims({
                page,
                limit,
                search: search ?? undefined,
                claimStatusTemplateId: claimStatusTemplateId ?? undefined,
                pipelineId: pipelineId ?? undefined,
                claimFolderId: claimFolderId ?? undefined,
                assigneeUserId: assigneeUserId ?? undefined,
                includeArchived: includeArchived || undefined,
                archivedOnly: archivedOnly || undefined,
            });
            return response;
        },
        placeholderData: keepPreviousData,
    });

    // Extract claims array from response or default to empty
    const claims = claimsResponse?.claims || [];
    const total = claimsResponse?.total ?? claims.length;
    const totalPages = Math.ceil(total / limit);

    const updateStatusMutation = useMutation({
        mutationFn: async ({ claimId, statusTemplateId }: { claimId: number; statusTemplateId: number }) => {
            return await apiClient.claims.updateClaim({
                claimId,
                updateClaimRequest: {
                    claimStatusTemplateId: statusTemplateId
                }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const updatePipelineMutation = useMutation({
        mutationFn: async ({ claimId, pipelineId }: { claimId: number; pipelineId: number }) => {
            return await apiClient.claims.updateClaim({
                claimId,
                updateClaimRequest: { pipelineId } satisfies UpdateClaimRequest,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
        },
        onError: async (error: Error) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Couldn't move claim", description: message, variant: "destructive" });
        },
    });

    const updateFolderMutation = useMutation({
        mutationFn: async ({ claimId, claimFolderId }: { claimId: number; claimFolderId: number | null }) => {
            return await apiClient.claims.updateClaim({
                claimId,
                updateClaimRequest: {
                    claimFolderId
                }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            // Refresh folder claim counts shown in the folder bar
            queryClient.invalidateQueries({ queryKey: ["/api/claim-folders"] });
        },
        onError: async (error: Error) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Couldn't move claim", description: message, variant: "destructive" });
        },
    });

    const deleteClaimMutation = useMutation({
        mutationFn: async (claimId: number) => {
            return await apiClient.claims.deleteClaim({ claimId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            toast({ title: "Claim deleted", variant: "default" });
        },
        onError: async (error: Error) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Cannot delete claim", description: message, variant: "destructive" });
        },
    });

    const setArchivedMutation = useMutation({
        mutationFn: async ({ claimId, isArchived }: { claimId: number; isArchived: boolean }) => {
            return await apiClient.claims.updateClaim({
                claimId,
                updateClaimRequest: {
                    isArchived,
                },
            });
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            toast({
                title: variables.isArchived ? "Claim archived" : "Claim restored",
                variant: "default",
            });
        },
        onError: async (error: Error, variables) => {
            const message = await getApiErrorMessage(error);
            toast({
                title: variables.isArchived ? "Cannot archive claim" : "Cannot restore claim",
                description: message,
                variant: "destructive",
            });
        },
    });

    const createClaimMutation = useMutation({
        mutationFn: async (claimData: any) => {
            return await apiClient.claims.createClaim({
                createClaimRequest: claimData
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const updateClaimMutation = useMutation({
        mutationFn: async ({ 
            claimId, 
            updateClaimRequest,
            isDraftFinalization 
        }: { 
            claimId: number; 
            updateClaimRequest: any;
            isDraftFinalization?: boolean;
        }) => {
            // If draft finalization, we need to manually call with query param
            // since the generated client doesn't support optional query parameters
            if (isDraftFinalization) {
                const response = await apiClient.claims['request']({
                    path: `/api/claims/{claimId}?isDraftFinalization=true`.replace(`{claimId}`, encodeURIComponent(String(claimId))),
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: updateClaimRequest,
                });
                
                return response.json();
            }
            
            // Normal update without the flag
            return await apiClient.claims.updateClaim({
                claimId,
                updateClaimRequest
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    return {
        claims,
        total,
        totalPages,
        isLoading: isLoadingClaims,
        isPlaceholderData,
        updateStatus: updateStatusMutation.mutate,
        updatePipeline: updatePipelineMutation.mutate,
        updateFolder: updateFolderMutation.mutate,
        createClaim: createClaimMutation.mutate,
        createClaimAsync: createClaimMutation.mutateAsync,
        updateClaim: updateClaimMutation.mutate,
        updateClaimAsync: updateClaimMutation.mutateAsync,
        deleteClaim: deleteClaimMutation.mutate,
        deleteClaimAsync: deleteClaimMutation.mutateAsync,
        setArchived: setArchivedMutation.mutate,
        setArchivedAsync: setArchivedMutation.mutateAsync,
        isUpdating: updateStatusMutation.isPending || updatePipelineMutation.isPending || updateClaimMutation.isPending,
        isCreating: createClaimMutation.isPending,
        isDeleting: deleteClaimMutation.isPending,
        isArchiving: setArchivedMutation.isPending,
    };
}
